import moment from "moment";
import MESSAGE from "../../../common/constants/MESSAGE";
import REGEX from "../../../common/constants/REGEX";

export type RowFieldErrors = { [field: string]: string };

export type RowValues = {
  lastName?: string;
  firstName?: string;
  lastNameKatakana?: string;
  firstNameKatakana?: string;
  dateOfBirth?: string;
  isForeigner?: boolean;
};

/** Bỏ mọi khoảng trắng half-size lẫn full-size */
export const normalizeName = (value?: string) =>
  (value ?? "").replace(/[\s\u3000]/g, "");

const isEmpty = (value?: string) => !normalizeName(value);

const NAME_PATTERNS = {
  // Không tick 外国籍: Kanji, Hiragana, Katakana full-size, số/chữ half-size, dấu cách
  allowedLocal: /^[A-Za-z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\s\u3000]+$/,
  invalidFullWidthSpecialChars: /[！”＃＄％＆’＊＋，−．／：；＜＝＞？＠［￥］＾＿‘｛｜｝]/,
  invalidHalfWidthSpecialChars: /[、。]/,
  // Có tick 外国籍: chỉ chữ in hoa + ký tự half-size, không cho dấu cách full-size
  allowedForeigner: /^[A-Z,'\-().\/\s]+$/,
  fullWidthSpace: /\u3000/,
};

/** Charset của 技能者姓① / 技能者名①. Trả "" nếu hợp lệ. */
export const validateName = (
  value: string | undefined,
  isForeigner: boolean | undefined,
  invalidCharsetMessage: string
): string => {
  if (!value) return "";

  if (isForeigner) {
    if (
      !NAME_PATTERNS.allowedForeigner.test(value) ||
      NAME_PATTERNS.fullWidthSpace.test(value)
    ) {
      return invalidCharsetMessage;
    }
  } else {
    if (!NAME_PATTERNS.allowedLocal.test(value)) return invalidCharsetMessage;

    const hasInvalidChars = [
      NAME_PATTERNS.invalidFullWidthSpecialChars,
      NAME_PATTERNS.invalidHalfWidthSpecialChars,
    ].some((pattern) => pattern.test(value));

    if (hasInvalidChars) return invalidCharsetMessage;
  }

  if (!REGEX.NOT_VIETNAM.test(normalizeName(value))) {
    return MESSAGE.MESSAGE_INVALID_INPUT;
  }

  return "";
};

/** Tên không có thông tin 外国籍: hợp lệ nếu pass một trong hai rule của CCUS. */
export const validateNameForAnyNationality = (
  value: string | undefined,
  invalidCharsetMessage: string
): string => {
  const localError = validateName(value, false, invalidCharsetMessage);
  const foreignerError = validateName(value, true, invalidCharsetMessage);

  return localError && foreignerError ? localError : "";
};

/** Charset của 技能者姓(カタカナ)② / 技能者名(カタカナ)②. Trả "" nếu hợp lệ. */
export const validateNameKatakana = (value: string | undefined): string => {
  if (!value) return "";

  const validHalfWidth = /^[A-Za-z0-9~!@#$%^&*()_+\[\]\;',.\/{}|:"<>?=\- ]+$/;
  const validFullWidthKatakana = /^[\u30A0-\u30FF]+$/;

  const hasInvalidChars = value
    .split("")
    .some(
      (char) => !validHalfWidth.test(char) && !validFullWidthKatakana.test(char)
    );

  return hasInvalidChars ? MESSAGE.MESSAGE_CCUS061_INPUT_KATAKANA : "";
};

/**
 * Tên chỉ có một ô nhập: hợp lệ nếu pass nhóm ① (nội địa hoặc ngoại quốc)
 * hoặc nhóm Katakana ② của CCUS.
 */
export const validateNameForAnyCCUSRule = (
  value: string | undefined,
  invalidCharsetMessage: string
): string => {
  const groupOneError = validateNameForAnyNationality(
    value,
    invalidCharsetMessage
  );
  const groupTwoError = validateNameKatakana(value);

  return groupOneError && groupTwoError ? groupOneError : "";
};

/** 生年月日: bắt buộc, đúng yyyyMMdd, và là ngày có thật. Trả "" nếu hợp lệ. */
export const validateDateOfBirth = (value: string | undefined): string => {
  if (!value) return MESSAGE.MESSAGE_REQUIRED_CCUS;
  if (!/^\d{8}$/.test(value)) return MESSAGE.MESSAGE_CCUS061_VALID_DATE;

  const [year, month, day] = [
    value.slice(0, 4),
    value.slice(4, 6),
    value.slice(6, 8),
  ];

  const isValidDate = moment(
    `${year}-${month}-${day}`,
    "YYYY-MM-DD",
    true
  ).isValid();

  if (
    Number(year) > new Date().getFullYear() ||
    !/^(0[1-9]|1[0-2])$/.test(month) ||
    !isValidDate
  ) {
    return MESSAGE.MESSAGE_CCUS061_VALID_DATE;
  }

  return "";
};

/**
 * So khớp 氏名 với (技能者姓①+技能者名①) hoặc (技能者姓②+技能者名②).
 * Đây là phần DUY NHẤT chạy realtime khi user gõ; trả "" nếu hợp lệ hoặc chưa đủ dữ kiện.
 */
export const validateFullNameMatch = (
  row: RowValues,
  fullName?: string
): string => {
  const groupOne = normalizeName(row.lastName) + normalizeName(row.firstName);
  const groupTwo =
    normalizeName(row.lastNameKatakana) + normalizeName(row.firstNameKatakana);

  // Chưa nhập ô tên nào, hoặc API không trả 氏名 thì chưa có gì để so
  if (!groupOne && !groupTwo) return "";
  const target = normalizeName(fullName);
  if (!target) return "";

  return groupOne !== target && groupTwo !== target
    ? MESSAGE.MESSAGE_CCUS061_NAME_NOT_MATCH
    : "";
};

/**
 * Validate đầy đủ 1 dòng (sai định dạng / thiếu / bỏ trống).
 * CHỈ chạy khi bấm submit trên các dòng đang tick — không chạy lúc gõ.
 */
export const validateRowCCUS062 = (
  row: RowValues,
  fullName?: string
): RowFieldErrors => {
  const errors: RowFieldErrors = {};

  const lastNameError = validateName(
    row.lastName,
    row.isForeigner,
    MESSAGE.MESSAGE_CCUS061_VALID_LAST_NAME
  );
  if (lastNameError) errors.lastName = lastNameError;

  const firstNameError = validateName(
    row.firstName,
    row.isForeigner,
    MESSAGE.MESSAGE_CCUS061_VALID_FIRST_NAME
  );
  if (firstNameError) errors.firstName = firstNameError;

  const lastNameKatakanaError = validateNameKatakana(row.lastNameKatakana);
  if (lastNameKatakanaError) errors.lastNameKatakana = lastNameKatakanaError;

  const firstNameKatakanaError = validateNameKatakana(row.firstNameKatakana);
  if (firstNameKatakanaError) errors.firstNameKatakana = firstNameKatakanaError;

  const groupOne = normalizeName(row.lastName) + normalizeName(row.firstName);
  const groupTwo =
    normalizeName(row.lastNameKatakana) + normalizeName(row.firstNameKatakana);

  // Bắt buộc nhập ①hoặc② — báo ngay dưới các ô liên quan
  if (!groupOne && !groupTwo) {
    errors.lastName = MESSAGE.MESSAGE_CCUS061_VALID;
    errors.firstName = MESSAGE.MESSAGE_CCUS061_VALID;
    errors.lastNameKatakana = MESSAGE.MESSAGE_CCUS061_VALID;
    errors.firstNameKatakana = MESSAGE.MESSAGE_CCUS061_VALID;
  }

  const fullNameError = validateFullNameMatch(row, fullName);
  if (fullNameError) errors.fullName = fullNameError;

  const dateOfBirthError = validateDateOfBirth(row.dateOfBirth);
  if (dateOfBirthError) errors.dateOfBirth = dateOfBirthError;

  return errors;
};

export const isEmptyRowValue = isEmpty;
